import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

/**
 * Calculate the next due date based on frequency and advanced options
 * @param {Date|string} currentDate - Current due date or initial due date
 * @param {string} frequency - Frequency type (Daily, Weekly, etc.)
 * @param {Object} options - Advanced frequency options
 * @returns {Date} Next due date
 */
export function calculateNextDueDate(currentDate, frequency, options = {}) {
  const {
    FrequencyDayOfWeek,
    FrequencyWeekType,
    FrequencyDayType,
    FrequencyDayNumber,
    FrequencyWeekday,
    FrequencyMonthType,
    FrequencyMonth,
    FrequencyYears
  } = options;

  let date = dayjs(currentDate);
  if (!date.isValid()) return null;

  switch (frequency) {
    case 'Daily':
      return date.add(1, 'day').toDate();

    case 'Weekly':
      if (FrequencyDayOfWeek !== null && FrequencyDayOfWeek !== undefined) {
        // Find next occurrence of the specified day of week
        let nextDate = date.add(1, 'week');
        while (nextDate.day() !== Number(FrequencyDayOfWeek)) {
          nextDate = nextDate.add(1, 'day');
        }
        return nextDate.toDate();
      }
      return date.add(1, 'week').toDate();

    case 'Bi-Weekly':
      if (FrequencyDayOfWeek !== null && FrequencyDayOfWeek !== undefined) {
        let nextDate = date.add(2, 'weeks');
        
        // Adjust to correct day of week
        while (nextDate.day() !== Number(FrequencyDayOfWeek)) {
          nextDate = nextDate.add(1, 'day');
        }
        
        // If week type is specified, ensure it's odd/even
        if (FrequencyWeekType) {
          const weekNumber = nextDate.week();
          const isOddWeek = weekNumber % 2 === 1;
          const needsOdd = FrequencyWeekType === 'Odd';
          
          if (isOddWeek !== needsOdd) {
            nextDate = nextDate.add(1, 'week');
          }
        }
        
        return nextDate.toDate();
      }
      return date.add(2, 'weeks').toDate();

    case 'Monthly':
      return calculateMonthlyDate(date, FrequencyDayType, FrequencyDayNumber, FrequencyWeekday, 1);

    case 'Bi-Monthly':
      let biMonthlyDate = calculateMonthlyDate(date, FrequencyDayType, FrequencyDayNumber, FrequencyWeekday, 2);
      
      // If month type is specified, ensure it's odd/even
      if (FrequencyWeekType && biMonthlyDate) {
        const targetDate = dayjs(biMonthlyDate);
        const monthNumber = targetDate.month() + 1; // 1-12
        const isOddMonth = monthNumber % 2 === 1;
        const needsOdd = FrequencyWeekType === 'Odd';
        
        if (isOddMonth !== needsOdd) {
          biMonthlyDate = calculateMonthlyDate(targetDate, FrequencyDayType, FrequencyDayNumber, FrequencyWeekday, 2);
        }
      }
      
      return biMonthlyDate;

    case 'Quarterly':
      let quarterlyDate = calculateMonthlyDate(date, FrequencyDayType, FrequencyDayNumber, FrequencyWeekday, 3);
      
      // Adjust for month of quarter if specified
      if (FrequencyMonthType && quarterlyDate) {
        const targetDate = dayjs(quarterlyDate);
        const monthInQuarter = (targetDate.month() % 3) + 1; // 1, 2, or 3
        let targetMonthInQuarter = 1;
        
        if (FrequencyMonthType === 'Second') targetMonthInQuarter = 2;
        else if (FrequencyMonthType === 'Third') targetMonthInQuarter = 3;
        
        if (monthInQuarter !== targetMonthInQuarter) {
          const monthsToAdd = targetMonthInQuarter - monthInQuarter;
          quarterlyDate = calculateMonthlyDate(
            targetDate.add(monthsToAdd, 'months'),
            FrequencyDayType,
            FrequencyDayNumber,
            FrequencyWeekday,
            0
          );
        }
      }
      
      return quarterlyDate;

    case 'Semi-Annually':
      // Semi-annual schedules have two valid months (6 months apart).
      // Move exactly 6 months from the current due date, then apply day rules.
      return calculateMonthlyDate(date, FrequencyDayType, FrequencyDayNumber, FrequencyWeekday, 6);

    case 'Annually':
      if (FrequencyMonth !== null && FrequencyMonth !== undefined) {
        return calculateYearlyDate(date, FrequencyDayType, FrequencyDayNumber, FrequencyWeekday, FrequencyMonth, 12);
      }
      return date.add(1, 'year').toDate();

    case 'Every x years':
      const years = FrequencyYears || 1;
      if (FrequencyMonth !== null && FrequencyMonth !== undefined) {
        return calculateYearlyDate(date, FrequencyDayType, FrequencyDayNumber, FrequencyWeekday, FrequencyMonth, years * 12);
      }
      return date.add(years, 'years').toDate();

    default:
      return date.add(1, 'month').toDate();
  }
}

/**
 * Calculate monthly date based on day type
 */
function calculateMonthlyDate(date, dayType, dayNumber, weekday, monthsToAdd) {
  let targetDate = dayjs(date).add(monthsToAdd, 'months');
  
  if (!dayType) {
    return targetDate.toDate();
  }
  
  switch (dayType) {
    case 'DayNumber':
      if (dayNumber) {
        targetDate = targetDate.date(Math.min(Number(dayNumber), targetDate.daysInMonth()));
      }
      break;
      
    case 'LastDay':
      // Always midnight — endOf('month') is 23:59:59 and formatDateOnly/parseDateOnly
      // read UTC components, which rolls Central evening dates to the next calendar day.
      targetDate = targetDate.endOf('month').startOf('day');
      break;
      
    case 'FirstWeekday':
    case 'SecondWeekday':
    case 'ThirdWeekday':
    case 'LastWeekday':
      if (weekday !== null && weekday !== undefined) {
        targetDate = getNthWeekdayOfMonth(targetDate, dayType, Number(weekday));
      }
      break;
  }
  
  return targetDate.toDate();
}

/**
 * Calculate yearly date (for semi-annual, annual, every x years)
 */
function calculateYearlyDate(date, dayType, dayNumber, weekday, targetMonth, monthsToAdd) {
  let currentDate = dayjs(date);
  let targetDate = currentDate.add(monthsToAdd, 'months');
  
  // Adjust to target month
  while (targetDate.month() + 1 !== Number(targetMonth)) {
    targetDate = targetDate.add(1, 'month');
    // Prevent infinite loop
    if (targetDate.year() > currentDate.year() + 10) break;
  }
  
  // Apply day type
  if (!dayType) {
    return targetDate.toDate();
  }
  
  switch (dayType) {
    case 'DayNumber':
      if (dayNumber) {
        targetDate = targetDate.date(Math.min(Number(dayNumber), targetDate.daysInMonth()));
      }
      break;
      
    case 'LastDay':
      // Always midnight — endOf('month') is 23:59:59 and formatDateOnly/parseDateOnly
      // read UTC components, which rolls Central evening dates to the next calendar day.
      targetDate = targetDate.endOf('month').startOf('day');
      break;
      
    case 'FirstWeekday':
    case 'SecondWeekday':
    case 'ThirdWeekday':
    case 'LastWeekday':
      if (weekday !== null && weekday !== undefined) {
        targetDate = getNthWeekdayOfMonth(targetDate, dayType, Number(weekday));
      }
      break;
  }
  
  return targetDate.toDate();
}

/**
 * Get the nth occurrence of a weekday in a month
 */
function getNthWeekdayOfMonth(date, occurrence, weekday) {
  const startOfMonth = date.startOf('month');
  
  if (occurrence === 'LastWeekday') {
    // Find last occurrence
    let targetDate = date.endOf('month').startOf('day');
    
    // Go back to find the last occurrence of the weekday
    while (targetDate.day() !== weekday) {
      targetDate = targetDate.subtract(1, 'day');
    }
    
    return targetDate;
  }
  
  // Find nth occurrence (First, Second, Third)
  let nthValue = 1;
  if (occurrence === 'SecondWeekday') nthValue = 2;
  else if (occurrence === 'ThirdWeekday') nthValue = 3;
  
  let count = 0;
  let targetDate = startOfMonth;
  
  // Find first occurrence of the weekday
  while (targetDate.day() !== weekday) {
    targetDate = targetDate.add(1, 'day');
  }
  count = 1;
  
  // Add weeks until we reach the nth occurrence
  while (count < nthValue) {
    targetDate = targetDate.add(1, 'week');
    count++;
  }
  
  return targetDate;
}

/**
 * Apply day-of-month rules to a date that is already positioned in the target month.
 */
function applyDayRule(date, dayType, dayNumber, weekday) {
  let targetDate = dayjs(date).startOf('day');

  if (!dayType) {
    return targetDate;
  }

  switch (dayType) {
    case 'DayNumber':
      if (dayNumber) {
        targetDate = targetDate.date(Math.min(Number(dayNumber), targetDate.daysInMonth()));
      }
      break;
    case 'LastDay':
      targetDate = targetDate.endOf('month').startOf('day');
      break;
    case 'FirstWeekday':
    case 'SecondWeekday':
    case 'ThirdWeekday':
    case 'LastWeekday':
      if (weekday !== null && weekday !== undefined) {
        targetDate = getNthWeekdayOfMonth(targetDate, dayType, Number(weekday)).startOf('day');
      }
      break;
    default:
      break;
  }

  return targetDate;
}

/**
 * Build the configured occurrence inside the current scheduling period (month/quarter/week).
 * Returns null when the current period cannot satisfy the configured constraints.
 */
function getCurrentPeriodOccurrence(referenceDate, frequency, options = {}) {
  const today = dayjs(referenceDate).startOf('day');
  const {
    FrequencyDayOfWeek,
    FrequencyWeekType,
    FrequencyDayType,
    FrequencyDayNumber,
    FrequencyWeekday,
    FrequencyMonthType
  } = options;

  switch (frequency) {
    case 'Monthly':
      if (!FrequencyDayType) return null;
      return applyDayRule(today.startOf('month'), FrequencyDayType, FrequencyDayNumber, FrequencyWeekday).toDate();

    case 'Bi-Monthly': {
      if (!FrequencyDayType) return null;
      const occurrence = applyDayRule(today.startOf('month'), FrequencyDayType, FrequencyDayNumber, FrequencyWeekday);
      if (FrequencyWeekType) {
        const monthNumber = occurrence.month() + 1;
        const isOddMonth = monthNumber % 2 === 1;
        const needsOdd = FrequencyWeekType === 'Odd';
        if (isOddMonth !== needsOdd) return null;
      }
      return occurrence.toDate();
    }

    case 'Quarterly': {
      if (!FrequencyDayType) return null;
      const quarterStartMonth = Math.floor(today.month() / 3) * 3;
      let targetMonthInQuarter = 1;
      if (FrequencyMonthType === 'Second') targetMonthInQuarter = 2;
      else if (FrequencyMonthType === 'Third') targetMonthInQuarter = 3;
      const targetMonth = quarterStartMonth + (targetMonthInQuarter - 1);
      const monthStart = dayjs(new Date(today.year(), targetMonth, 1));
      return applyDayRule(monthStart, FrequencyDayType, FrequencyDayNumber, FrequencyWeekday).toDate();
    }

    case 'Weekly': {
      if (FrequencyDayOfWeek === null || FrequencyDayOfWeek === undefined) return null;
      let candidate = today.add(1, 'day');
      for (let i = 0; i < 7; i += 1) {
        if (candidate.day() === Number(FrequencyDayOfWeek)) {
          return candidate.toDate();
        }
        candidate = candidate.add(1, 'day');
      }
      return null;
    }

    case 'Bi-Weekly': {
      if (FrequencyDayOfWeek === null || FrequencyDayOfWeek === undefined) return null;
      let candidate = today.add(1, 'day');
      for (let i = 0; i < 366; i += 1) {
        if (candidate.day() === Number(FrequencyDayOfWeek)) {
          if (!FrequencyWeekType) return candidate.toDate();
          const isOddWeek = candidate.week() % 2 === 1;
          const needsOdd = FrequencyWeekType === 'Odd';
          if (isOddWeek === needsOdd) return candidate.toDate();
        }
        candidate = candidate.add(1, 'day');
      }
      return null;
    }

    default:
      return null;
  }
}

/**
 * Calculate when a record should be generated (due date minus GenerateDaysBefore)
 * If the calculated date is in the past, advance to the next due occurrence.
 */
export function calculateGenerateDate(dueDate, generateDaysBefore, frequency, options = {}) {
  let currentDue = dayjs(dueDate);
  if (!currentDue.isValid()) return null;

  const today = dayjs().startOf('day');
  const daysBefore = generateDaysBefore || 0;

  for (let i = 0; i < 100; i += 1) {
    const generateDate = currentDue.subtract(daysBefore, 'days');
    if (generateDate.isAfter(today, 'day')) {
      return generateDate.toDate();
    }

    const nextDueDate = calculateNextDueDate(currentDue.toDate(), frequency, options);
    if (!nextDueDate) break;

    const nextDue = dayjs(nextDueDate).startOf('day');
    if (!nextDue.isAfter(currentDue, 'day')) break;
    currentDue = nextDue;
  }

  return currentDue.subtract(daysBefore, 'days').toDate();
}

/**
 * Get maximum allowed GenerateDaysBefore based on frequency
 */
export function getMaxGenerateDaysBefore(frequency, frequencyYears = 1) {
  // Ensure frequencyYears is a valid number (default to 1 if null, undefined, 0, or NaN)
  const years = (frequencyYears && Number(frequencyYears) > 0) ? Number(frequencyYears) : 1;
  
  switch (frequency) {
    case 'Daily':
      return 0;
    case 'Weekly':
      return 6;
    case 'Bi-Weekly':
      return 13;
    case 'Monthly':
      return 28;
    case 'Bi-Monthly':
      return 58;
    case 'Quarterly':
      return 88;
    case 'Semi-Annually':
      return 178;
    case 'Annually':
      return 363;
    case 'Every x years':
      return (years * 365) - 2;
    default:
      return 28;
  }
}

/**
 * Calculate initial due date from today based on frequency and options
 * Finds the next date from today that matches the frequency parameters
 */
export function calculateInitialDueDate(frequency, options = {}) {
  const today = dayjs().startOf('day');
  
  if (!frequency) return null;
  
  // Start with tomorrow as the earliest possible date
  let candidateDate = today.add(1, 'day');
  
  // For simple frequencies, just add the interval
  if (frequency === 'Daily') {
    return candidateDate.toDate();
  }
  
  // For period-based frequencies, first check if the current period's occurrence is still upcoming.
  const periodFrequencies = ['Monthly', 'Bi-Monthly', 'Quarterly', 'Weekly', 'Bi-Weekly'];
  if (periodFrequencies.includes(frequency)) {
    const currentPeriodDate = getCurrentPeriodOccurrence(today, frequency, options);
    if (currentPeriodDate) {
      const currentPeriodDayjs = dayjs(currentPeriodDate).startOf('day');
      if (currentPeriodDayjs.isAfter(today, 'day')) {
        return currentPeriodDate;
      }
    }
  }

  // For year-based frequencies (semi-annual/annual/every x years), we need to find the nearest
  // configured occurrence that is in the future. Using calculateNextDueDate(today, ...) for these
  // can incorrectly skip the current year's occurrence (e.g. November -> next becomes Nov of next year).
  const buildMonthOccurrence = (year, month) => {
    // month: 1-12
    let d = dayjs(new Date(year, Number(month) - 1, 1)).startOf('day');
    const dayType = options.FrequencyDayType;
    const dayNumber = options.FrequencyDayNumber;
    const weekday = options.FrequencyWeekday;

    if (!dayType) return d.toDate();

    switch (dayType) {
      case 'DayNumber': {
        if (dayNumber) {
          d = d.date(Math.min(Number(dayNumber), d.daysInMonth()));
        }
        break;
      }
      case 'LastDay': {
        d = d.endOf('month').startOf('day');
        break;
      }
      case 'FirstWeekday':
      case 'SecondWeekday':
      case 'ThirdWeekday':
      case 'LastWeekday': {
        if (weekday !== null && weekday !== undefined) {
          d = getNthWeekdayOfMonth(d, dayType, Number(weekday)).startOf('day');
        }
        break;
      }
      default:
        break;
    }

    return d.toDate();
  };

  if (frequency === 'Annually' && options.FrequencyMonth != null) {
    const month = Number(options.FrequencyMonth);
    const year0 = today.year();
    const thisYear = dayjs(buildMonthOccurrence(year0, month)).startOf('day');
    if (thisYear.isAfter(today, 'day')) return thisYear.toDate();
    const nextYear = dayjs(buildMonthOccurrence(year0 + 1, month)).startOf('day');
    return nextYear.toDate();
  }

  if (frequency === 'Semi-Annually' && options.FrequencyMonth != null) {
    const monthA = Number(options.FrequencyMonth);
    const monthB = ((monthA + 6 - 1) % 12) + 1; // wrap 1-12
    const year0 = today.year();
    const candidates = [
      dayjs(buildMonthOccurrence(year0, monthA)).startOf('day'),
      dayjs(buildMonthOccurrence(year0, monthB)).startOf('day'),
      dayjs(buildMonthOccurrence(year0 + 1, monthA)).startOf('day'),
      dayjs(buildMonthOccurrence(year0 + 1, monthB)).startOf('day'),
    ].filter(d => d.isAfter(today, 'day'));

    if (candidates.length) {
      candidates.sort((a, b) => a.valueOf() - b.valueOf());
      return candidates[0].toDate();
    }
  }

  if (frequency === 'Every x years' && options.FrequencyMonth != null) {
    const month = Number(options.FrequencyMonth);
    const stepYears = (options.FrequencyYears && Number(options.FrequencyYears) > 0) ? Number(options.FrequencyYears) : 1;
    let year = today.year();
    let candidate = dayjs(buildMonthOccurrence(year, month)).startOf('day');
    if (!candidate.isAfter(today, 'day')) {
      year += stepYears;
      candidate = dayjs(buildMonthOccurrence(year, month)).startOf('day');
    }
    return candidate.toDate();
  }
  
  // Advance from the current period occurrence when it has already passed, otherwise from today.
  const currentPeriodDate = getCurrentPeriodOccurrence(today, frequency, options);
  const baseDate = currentPeriodDate || today.toDate();
  let searchDate = calculateNextDueDate(baseDate, frequency, options);

  if (!searchDate) {
    return candidateDate.add(1, 'month').toDate();
  }

  let searchDateDayjs = dayjs(searchDate).startOf('day');
  if (!searchDateDayjs.isAfter(today, 'day')) {
    searchDate = calculateNextDueDate(searchDate, frequency, options);
  }

  return searchDate;
}

/**
 * Calculate the previous due date (working backwards)
 * Used for creating historical records
 */
export function calculatePreviousDueDate(currentDueDate, frequency, options = {}) {
  const date = dayjs(currentDueDate);
  if (!date.isValid()) return null;
  
  switch (frequency) {
    case 'Daily':
      return date.subtract(1, 'day').toDate();
      
    case 'Weekly':
      if (options.FrequencyDayOfWeek !== null && options.FrequencyDayOfWeek !== undefined) {
        // Go back to previous occurrence of the specified day
        let prevDate = date.subtract(1, 'week');
        while (prevDate.day() !== Number(options.FrequencyDayOfWeek)) {
          prevDate = prevDate.subtract(1, 'day');
        }
        return prevDate.toDate();
      }
      return date.subtract(1, 'week').toDate();
      
    case 'Bi-Weekly':
      if (options.FrequencyDayOfWeek !== null && options.FrequencyDayOfWeek !== undefined) {
        let prevDate = date.subtract(2, 'weeks');
        
        // Adjust to correct day of week
        while (prevDate.day() !== Number(options.FrequencyDayOfWeek)) {
          prevDate = prevDate.subtract(1, 'day');
        }
        
        // Handle odd/even weeks if specified
        if (options.FrequencyWeekType) {
          const weekNumber = prevDate.week();
          const isOddWeek = weekNumber % 2 === 1;
          const needsOdd = options.FrequencyWeekType === 'Odd';
          
          if (isOddWeek !== needsOdd) {
            prevDate = prevDate.subtract(1, 'week');
          }
        }
        
        return prevDate.toDate();
      }
      return date.subtract(2, 'weeks').toDate();
      
    case 'Monthly':
      return calculateMonthlyDateBackwards(date, options.FrequencyDayType, options.FrequencyDayNumber, options.FrequencyWeekday, 1);
      
    case 'Bi-Monthly':
      let biMonthlyDate = calculateMonthlyDateBackwards(date, options.FrequencyDayType, options.FrequencyDayNumber, options.FrequencyWeekday, 2);
      
      if (options.FrequencyWeekType && biMonthlyDate) {
        const targetDate = dayjs(biMonthlyDate);
        const monthNumber = targetDate.month() + 1;
        const isOddMonth = monthNumber % 2 === 1;
        const needsOdd = options.FrequencyWeekType === 'Odd';
        
        if (isOddMonth !== needsOdd) {
          biMonthlyDate = calculateMonthlyDateBackwards(targetDate, options.FrequencyDayType, options.FrequencyDayNumber, options.FrequencyWeekday, 2);
        }
      }
      
      return biMonthlyDate;
      
    case 'Quarterly':
      return calculateMonthlyDateBackwards(date, options.FrequencyDayType, options.FrequencyDayNumber, options.FrequencyWeekday, 3);
      
    case 'Semi-Annually':
      // Work backwards by the semi-annual interval and keep day rules in sync.
      return calculateMonthlyDateBackwards(date, options.FrequencyDayType, options.FrequencyDayNumber, options.FrequencyWeekday, 6);
      
    case 'Annually':
      if (options.FrequencyMonth !== null && options.FrequencyMonth !== undefined) {
        return calculateYearlyDateBackwards(date, options.FrequencyDayType, options.FrequencyDayNumber, options.FrequencyWeekday, options.FrequencyMonth, 12);
      }
      return date.subtract(1, 'year').toDate();
      
    case 'Every x years':
      const years = options.FrequencyYears || 1;
      if (options.FrequencyMonth !== null && options.FrequencyMonth !== undefined) {
        return calculateYearlyDateBackwards(date, options.FrequencyDayType, options.FrequencyDayNumber, options.FrequencyWeekday, options.FrequencyMonth, years * 12);
      }
      return date.subtract(years, 'years').toDate();
      
    default:
      return date.subtract(1, 'month').toDate();
  }
}

/**
 * Calculate monthly date backwards
 */
function calculateMonthlyDateBackwards(date, dayType, dayNumber, weekday, monthsToSubtract) {
  let targetDate = dayjs(date).subtract(monthsToSubtract, 'months');
  
  if (!dayType) {
    return targetDate.toDate();
  }
  
  switch (dayType) {
    case 'DayNumber':
      if (dayNumber) {
        targetDate = targetDate.date(Math.min(Number(dayNumber), targetDate.daysInMonth()));
      }
      break;
      
    case 'LastDay':
      // Always midnight — endOf('month') is 23:59:59 and formatDateOnly/parseDateOnly
      // read UTC components, which rolls Central evening dates to the next calendar day.
      targetDate = targetDate.endOf('month').startOf('day');
      break;
      
    case 'FirstWeekday':
    case 'SecondWeekday':
    case 'ThirdWeekday':
    case 'LastWeekday':
      if (weekday !== null && weekday !== undefined) {
        targetDate = getNthWeekdayOfMonth(targetDate, dayType, Number(weekday));
      }
      break;
  }
  
  return targetDate.toDate();
}

/**
 * Calculate yearly date backwards
 */
function calculateYearlyDateBackwards(date, dayType, dayNumber, weekday, targetMonth, monthsToSubtract) {
  let currentDate = dayjs(date);
  let targetDate = currentDate.subtract(monthsToSubtract, 'months');
  
  // Adjust to target month
  while (targetDate.month() + 1 !== Number(targetMonth)) {
    targetDate = targetDate.subtract(1, 'month');
    // Prevent infinite loop
    if (currentDate.diff(targetDate, 'years') > 10) break;
  }
  
  // Apply day type
  if (!dayType) {
    return targetDate.toDate();
  }
  
  switch (dayType) {
    case 'DayNumber':
      if (dayNumber) {
        targetDate = targetDate.date(Math.min(Number(dayNumber), targetDate.daysInMonth()));
      }
      break;
      
    case 'LastDay':
      // Always midnight — endOf('month') is 23:59:59 and formatDateOnly/parseDateOnly
      // read UTC components, which rolls Central evening dates to the next calendar day.
      targetDate = targetDate.endOf('month').startOf('day');
      break;
      
    case 'FirstWeekday':
    case 'SecondWeekday':
    case 'ThirdWeekday':
    case 'LastWeekday':
      if (weekday !== null && weekday !== undefined) {
        targetDate = getNthWeekdayOfMonth(targetDate, dayType, Number(weekday));
      }
      break;
  }
  
  return targetDate.toDate();
}



